/**
 * countries.ts — перечень стран для фильтров кабинета (WEB-1362 п.4).
 *
 * ISO 3166-1 alpha-2, ПОЛНЫЙ официальный перечень (249 записей),
 * отсортированный по английскому названию. Заведён решением владельца 02.09
 * по карточке WEB-1362: до него фильтр стран A/B-теста жил зашитым в
 * `CreateSmartLinkModal.tsx` списком из 35 значений, и нацелить направление
 * на Вьетнам, Аргентину, Колумбию, Индонезию и полторы сотни других стран
 * через кабинет было нельзя вовсе.
 *
 * Ограничение было ЧИСТО ФРОНТОВЫМ: сервер принимает произвольный массив
 * кодов без белого списка (`backend/src/smart-links/dto/create-direction.dto.ts`),
 * а маршрутизатор трактует ПУСТОЙ список как «все страны мира»
 * (`smart-link-router.service.ts`: `countries.length === 0 → true`). Поэтому
 * второго конца — серверного перечня, с которым это надо было бы сверять, —
 * у реестра нет по построению; см. строку в
 * `backend/src/shared/guards/two-ended-rules.inventory.md`.
 *
 * Подписи — английские (как и было в прежнем списке): коды приходят от
 * Cloudflare (`cf-ipcountry`) в том же алфавите, а поиск по выпадающему
 * списку работает по подписи.
 *
 * Самосогласованность реестра и его связь с европейским подмножеством
 * (`utils/analytics/euConsent.ts`) держит
 * `constants/__tests__/countries.web1362.test.ts`.
 */

export interface CountryOption {
  /** ISO 3166-1 alpha-2. */
  value: string;
  /** Английское название страны. */
  label: string;
  /**
   * Русское название страны (WEB-2168).
   *
   * ПОЧЕМУ ЗДЕСЬ, А НЕ ВТОРЫМ СПИСКОМ. До WEB-2168 русские названия жили
   * отдельной картой `RUSSIAN_COUNTRY_NAMES` в словаре аналитики и покрывали
   * 35 стран из 249 — остальные в русском кабинете молча показывались
   * по-английски. Два списка одних и тех же стран — это реестр с двумя
   * концами, который обязан разъехаться (CLAUDE.md, WEB-1844). Пара названий
   * лежит в ОДНОЙ записи, поэтому второго конца нет по построению и сторожить
   * их совпадение нечем и незачем; сторож проверяет другое — что у каждой
   * записи русское название есть и оно действительно русское
   * (`__tests__/countries.web2168.test.ts`).
   */
  labelRu: string;
}

export const COUNTRY_OPTIONS: ReadonlyArray<CountryOption> = [
  { value: 'AF', label: 'Afghanistan', labelRu: 'Афганистан' },
  { value: 'AX', label: 'Åland Islands', labelRu: 'Аландские острова' },
  { value: 'AL', label: 'Albania', labelRu: 'Албания' },
  { value: 'DZ', label: 'Algeria', labelRu: 'Алжир' },
  { value: 'AS', label: 'American Samoa', labelRu: 'Американское Самоа' },
  { value: 'AD', label: 'Andorra', labelRu: 'Андорра' },
  { value: 'AO', label: 'Angola', labelRu: 'Ангола' },
  { value: 'AI', label: 'Anguilla', labelRu: 'Ангилья' },
  { value: 'AQ', label: 'Antarctica', labelRu: 'Антарктида' },
  { value: 'AG', label: 'Antigua & Barbuda', labelRu: 'Антигуа и Барбуда' },
  { value: 'AR', label: 'Argentina', labelRu: 'Аргентина' },
  { value: 'AM', label: 'Armenia', labelRu: 'Армения' },
  { value: 'AW', label: 'Aruba', labelRu: 'Аруба' },
  { value: 'AU', label: 'Australia', labelRu: 'Австралия' },
  { value: 'AT', label: 'Austria', labelRu: 'Австрия' },
  { value: 'AZ', label: 'Azerbaijan', labelRu: 'Азербайджан' },
  { value: 'BS', label: 'Bahamas', labelRu: 'Багамы' },
  { value: 'BH', label: 'Bahrain', labelRu: 'Бахрейн' },
  { value: 'BD', label: 'Bangladesh', labelRu: 'Бангладеш' },
  { value: 'BB', label: 'Barbados', labelRu: 'Барбадос' },
  { value: 'BY', label: 'Belarus', labelRu: 'Беларусь' },
  { value: 'BE', label: 'Belgium', labelRu: 'Бельгия' },
  { value: 'BZ', label: 'Belize', labelRu: 'Белиз' },
  { value: 'BJ', label: 'Benin', labelRu: 'Бенин' },
  { value: 'BM', label: 'Bermuda', labelRu: 'Бермудские острова' },
  { value: 'BT', label: 'Bhutan', labelRu: 'Бутан' },
  { value: 'BO', label: 'Bolivia', labelRu: 'Боливия' },
  { value: 'BA', label: 'Bosnia & Herzegovina', labelRu: 'Босния и Герцеговина' },
  { value: 'BW', label: 'Botswana', labelRu: 'Ботсвана' },
  { value: 'BV', label: 'Bouvet Island', labelRu: 'Остров Буве' },
  { value: 'BR', label: 'Brazil', labelRu: 'Бразилия' },
  { value: 'IO', label: 'British Indian Ocean Territory', labelRu: 'Британская территория в Индийском океане' },
  { value: 'VG', label: 'British Virgin Islands', labelRu: 'Виргинские острова (Великобритания)' },
  { value: 'BN', label: 'Brunei', labelRu: 'Бруней' },
  { value: 'BG', label: 'Bulgaria', labelRu: 'Болгария' },
  { value: 'BF', label: 'Burkina Faso', labelRu: 'Буркина-Фасо' },
  { value: 'BI', label: 'Burundi', labelRu: 'Бурунди' },
  { value: 'KH', label: 'Cambodia', labelRu: 'Камбоджа' },
  { value: 'CM', label: 'Cameroon', labelRu: 'Камерун' },
  { value: 'CA', label: 'Canada', labelRu: 'Канада' },
  { value: 'CV', label: 'Cape Verde', labelRu: 'Кабо-Верде' },
  { value: 'BQ', label: 'Caribbean Netherlands', labelRu: 'Бонэйр, Синт-Эстатиус и Саба' },
  { value: 'KY', label: 'Cayman Islands', labelRu: 'Острова Кайман' },
  { value: 'CF', label: 'Central African Republic', labelRu: 'Центрально-Африканская Республика' },
  { value: 'TD', label: 'Chad', labelRu: 'Чад' },
  { value: 'CL', label: 'Chile', labelRu: 'Чили' },
  { value: 'CN', label: 'China', labelRu: 'Китай' },
  { value: 'CX', label: 'Christmas Island', labelRu: 'Остров Рождества' },
  { value: 'CC', label: 'Cocos (Keeling) Islands', labelRu: 'Кокосовые острова' },
  { value: 'CO', label: 'Colombia', labelRu: 'Колумбия' },
  { value: 'KM', label: 'Comoros', labelRu: 'Коморы' },
  { value: 'CG', label: 'Congo - Brazzaville', labelRu: 'Конго (Браззавиль)' },
  { value: 'CD', label: 'Congo - Kinshasa', labelRu: 'Конго (Киншаса)' },
  { value: 'CK', label: 'Cook Islands', labelRu: 'Острова Кука' },
  { value: 'CR', label: 'Costa Rica', labelRu: 'Коста-Рика' },
  { value: 'CI', label: 'Côte d’Ivoire', labelRu: 'Кот-д’Ивуар' },
  { value: 'HR', label: 'Croatia', labelRu: 'Хорватия' },
  { value: 'CU', label: 'Cuba', labelRu: 'Куба' },
  { value: 'CW', label: 'Curaçao', labelRu: 'Кюрасао' },
  { value: 'CY', label: 'Cyprus', labelRu: 'Кипр' },
  { value: 'CZ', label: 'Czechia', labelRu: 'Чехия' },
  { value: 'DK', label: 'Denmark', labelRu: 'Дания' },
  { value: 'DJ', label: 'Djibouti', labelRu: 'Джибути' },
  { value: 'DM', label: 'Dominica', labelRu: 'Доминика' },
  { value: 'DO', label: 'Dominican Republic', labelRu: 'Доминиканская Республика' },
  { value: 'EC', label: 'Ecuador', labelRu: 'Эквадор' },
  { value: 'EG', label: 'Egypt', labelRu: 'Египет' },
  { value: 'SV', label: 'El Salvador', labelRu: 'Сальвадор' },
  { value: 'GQ', label: 'Equatorial Guinea', labelRu: 'Экваториальная Гвинея' },
  { value: 'ER', label: 'Eritrea', labelRu: 'Эритрея' },
  { value: 'EE', label: 'Estonia', labelRu: 'Эстония' },
  { value: 'SZ', label: 'Eswatini', labelRu: 'Эсватини' },
  { value: 'ET', label: 'Ethiopia', labelRu: 'Эфиопия' },
  { value: 'FK', label: 'Falkland Islands', labelRu: 'Фолклендские острова' },
  { value: 'FO', label: 'Faroe Islands', labelRu: 'Фарерские острова' },
  { value: 'FJ', label: 'Fiji', labelRu: 'Фиджи' },
  { value: 'FI', label: 'Finland', labelRu: 'Финляндия' },
  { value: 'FR', label: 'France', labelRu: 'Франция' },
  { value: 'GF', label: 'French Guiana', labelRu: 'Французская Гвиана' },
  { value: 'PF', label: 'French Polynesia', labelRu: 'Французская Полинезия' },
  { value: 'TF', label: 'French Southern Territories', labelRu: 'Французские Южные территории' },
  { value: 'GA', label: 'Gabon', labelRu: 'Габон' },
  { value: 'GM', label: 'Gambia', labelRu: 'Гамбия' },
  { value: 'GE', label: 'Georgia', labelRu: 'Грузия' },
  { value: 'DE', label: 'Germany', labelRu: 'Германия' },
  { value: 'GH', label: 'Ghana', labelRu: 'Гана' },
  { value: 'GI', label: 'Gibraltar', labelRu: 'Гибралтар' },
  { value: 'GR', label: 'Greece', labelRu: 'Греция' },
  { value: 'GL', label: 'Greenland', labelRu: 'Гренландия' },
  { value: 'GD', label: 'Grenada', labelRu: 'Гренада' },
  { value: 'GP', label: 'Guadeloupe', labelRu: 'Гваделупа' },
  { value: 'GU', label: 'Guam', labelRu: 'Гуам' },
  { value: 'GT', label: 'Guatemala', labelRu: 'Гватемала' },
  { value: 'GG', label: 'Guernsey', labelRu: 'Гернси' },
  { value: 'GN', label: 'Guinea', labelRu: 'Гвинея' },
  { value: 'GW', label: 'Guinea-Bissau', labelRu: 'Гвинея-Бисау' },
  { value: 'GY', label: 'Guyana', labelRu: 'Гайана' },
  { value: 'HT', label: 'Haiti', labelRu: 'Гаити' },
  { value: 'HM', label: 'Heard & McDonald Islands', labelRu: 'Острова Херд и Макдональд' },
  { value: 'HN', label: 'Honduras', labelRu: 'Гондурас' },
  { value: 'HK', label: 'Hong Kong SAR China', labelRu: 'Гонконг' },
  { value: 'HU', label: 'Hungary', labelRu: 'Венгрия' },
  { value: 'IS', label: 'Iceland', labelRu: 'Исландия' },
  { value: 'IN', label: 'India', labelRu: 'Индия' },
  { value: 'ID', label: 'Indonesia', labelRu: 'Индонезия' },
  { value: 'IR', label: 'Iran', labelRu: 'Иран' },
  { value: 'IQ', label: 'Iraq', labelRu: 'Ирак' },
  { value: 'IE', label: 'Ireland', labelRu: 'Ирландия' },
  { value: 'IM', label: 'Isle of Man', labelRu: 'Остров Мэн' },
  { value: 'IL', label: 'Israel', labelRu: 'Израиль' },
  { value: 'IT', label: 'Italy', labelRu: 'Италия' },
  { value: 'JM', label: 'Jamaica', labelRu: 'Ямайка' },
  { value: 'JP', label: 'Japan', labelRu: 'Япония' },
  { value: 'JE', label: 'Jersey', labelRu: 'Джерси' },
  { value: 'JO', label: 'Jordan', labelRu: 'Иордания' },
  { value: 'KZ', label: 'Kazakhstan', labelRu: 'Казахстан' },
  { value: 'KE', label: 'Kenya', labelRu: 'Кения' },
  { value: 'KI', label: 'Kiribati', labelRu: 'Кирибати' },
  { value: 'KW', label: 'Kuwait', labelRu: 'Кувейт' },
  { value: 'KG', label: 'Kyrgyzstan', labelRu: 'Киргизия' },
  { value: 'LA', label: 'Laos', labelRu: 'Лаос' },
  { value: 'LV', label: 'Latvia', labelRu: 'Латвия' },
  { value: 'LB', label: 'Lebanon', labelRu: 'Ливан' },
  { value: 'LS', label: 'Lesotho', labelRu: 'Лесото' },
  { value: 'LR', label: 'Liberia', labelRu: 'Либерия' },
  { value: 'LY', label: 'Libya', labelRu: 'Ливия' },
  { value: 'LI', label: 'Liechtenstein', labelRu: 'Лихтенштейн' },
  { value: 'LT', label: 'Lithuania', labelRu: 'Литва' },
  { value: 'LU', label: 'Luxembourg', labelRu: 'Люксембург' },
  { value: 'MO', label: 'Macao SAR China', labelRu: 'Макао' },
  { value: 'MG', label: 'Madagascar', labelRu: 'Мадагаскар' },
  { value: 'MW', label: 'Malawi', labelRu: 'Малави' },
  { value: 'MY', label: 'Malaysia', labelRu: 'Малайзия' },
  { value: 'MV', label: 'Maldives', labelRu: 'Мальдивы' },
  { value: 'ML', label: 'Mali', labelRu: 'Мали' },
  { value: 'MT', label: 'Malta', labelRu: 'Мальта' },
  { value: 'MH', label: 'Marshall Islands', labelRu: 'Маршалловы острова' },
  { value: 'MQ', label: 'Martinique', labelRu: 'Мартиника' },
  { value: 'MR', label: 'Mauritania', labelRu: 'Мавритания' },
  { value: 'MU', label: 'Mauritius', labelRu: 'Маврикий' },
  { value: 'YT', label: 'Mayotte', labelRu: 'Майотта' },
  { value: 'MX', label: 'Mexico', labelRu: 'Мексика' },
  { value: 'FM', label: 'Micronesia', labelRu: 'Федеративные Штаты Микронезии' },
  { value: 'MD', label: 'Moldova', labelRu: 'Молдова' },
  { value: 'MC', label: 'Monaco', labelRu: 'Монако' },
  { value: 'MN', label: 'Mongolia', labelRu: 'Монголия' },
  { value: 'ME', label: 'Montenegro', labelRu: 'Черногория' },
  { value: 'MS', label: 'Montserrat', labelRu: 'Монтсеррат' },
  { value: 'MA', label: 'Morocco', labelRu: 'Марокко' },
  { value: 'MZ', label: 'Mozambique', labelRu: 'Мозамбик' },
  { value: 'MM', label: 'Myanmar (Burma)', labelRu: 'Мьянма (Бирма)' },
  { value: 'NA', label: 'Namibia', labelRu: 'Намибия' },
  { value: 'NR', label: 'Nauru', labelRu: 'Науру' },
  { value: 'NP', label: 'Nepal', labelRu: 'Непал' },
  { value: 'NL', label: 'Netherlands', labelRu: 'Нидерланды' },
  { value: 'NC', label: 'New Caledonia', labelRu: 'Новая Каледония' },
  { value: 'NZ', label: 'New Zealand', labelRu: 'Новая Зеландия' },
  { value: 'NI', label: 'Nicaragua', labelRu: 'Никарагуа' },
  { value: 'NE', label: 'Niger', labelRu: 'Нигер' },
  { value: 'NG', label: 'Nigeria', labelRu: 'Нигерия' },
  { value: 'NU', label: 'Niue', labelRu: 'Ниуэ' },
  { value: 'NF', label: 'Norfolk Island', labelRu: 'Остров Норфолк' },
  { value: 'KP', label: 'North Korea', labelRu: 'КНДР' },
  { value: 'MK', label: 'North Macedonia', labelRu: 'Северная Македония' },
  { value: 'MP', label: 'Northern Mariana Islands', labelRu: 'Северные Марианские острова' },
  { value: 'NO', label: 'Norway', labelRu: 'Норвегия' },
  { value: 'OM', label: 'Oman', labelRu: 'Оман' },
  { value: 'PK', label: 'Pakistan', labelRu: 'Пакистан' },
  { value: 'PW', label: 'Palau', labelRu: 'Палау' },
  { value: 'PS', label: 'Palestinian Territories', labelRu: 'Палестинские территории' },
  { value: 'PA', label: 'Panama', labelRu: 'Панама' },
  { value: 'PG', label: 'Papua New Guinea', labelRu: 'Папуа — Новая Гвинея' },
  { value: 'PY', label: 'Paraguay', labelRu: 'Парагвай' },
  { value: 'PE', label: 'Peru', labelRu: 'Перу' },
  { value: 'PH', label: 'Philippines', labelRu: 'Филиппины' },
  { value: 'PN', label: 'Pitcairn Islands', labelRu: 'Острова Питкэрн' },
  { value: 'PL', label: 'Poland', labelRu: 'Польша' },
  { value: 'PT', label: 'Portugal', labelRu: 'Португалия' },
  { value: 'PR', label: 'Puerto Rico', labelRu: 'Пуэрто-Рико' },
  { value: 'QA', label: 'Qatar', labelRu: 'Катар' },
  { value: 'RE', label: 'Réunion', labelRu: 'Реюньон' },
  { value: 'RO', label: 'Romania', labelRu: 'Румыния' },
  { value: 'RU', label: 'Russia', labelRu: 'Россия' },
  { value: 'RW', label: 'Rwanda', labelRu: 'Руанда' },
  { value: 'WS', label: 'Samoa', labelRu: 'Самоа' },
  { value: 'SM', label: 'San Marino', labelRu: 'Сан-Марино' },
  { value: 'ST', label: 'São Tomé & Príncipe', labelRu: 'Сан-Томе и Принсипи' },
  { value: 'SA', label: 'Saudi Arabia', labelRu: 'Саудовская Аравия' },
  { value: 'SN', label: 'Senegal', labelRu: 'Сенегал' },
  { value: 'RS', label: 'Serbia', labelRu: 'Сербия' },
  { value: 'SC', label: 'Seychelles', labelRu: 'Сейшельские острова' },
  { value: 'SL', label: 'Sierra Leone', labelRu: 'Сьерра-Леоне' },
  { value: 'SG', label: 'Singapore', labelRu: 'Сингапур' },
  { value: 'SX', label: 'Sint Maarten', labelRu: 'Синт-Мартен' },
  { value: 'SK', label: 'Slovakia', labelRu: 'Словакия' },
  { value: 'SI', label: 'Slovenia', labelRu: 'Словения' },
  { value: 'SB', label: 'Solomon Islands', labelRu: 'Соломоновы острова' },
  { value: 'SO', label: 'Somalia', labelRu: 'Сомали' },
  { value: 'ZA', label: 'South Africa', labelRu: 'ЮАР' },
  { value: 'GS', label: 'South Georgia & South Sandwich Islands', labelRu: 'Южная Георгия и Южные Сандвичевы острова' },
  { value: 'KR', label: 'South Korea', labelRu: 'Южная Корея' },
  { value: 'SS', label: 'South Sudan', labelRu: 'Южный Судан' },
  { value: 'ES', label: 'Spain', labelRu: 'Испания' },
  { value: 'LK', label: 'Sri Lanka', labelRu: 'Шри-Ланка' },
  { value: 'BL', label: 'St. Barthélemy', labelRu: 'Сен-Бартелеми' },
  { value: 'SH', label: 'St. Helena', labelRu: 'Остров Святой Елены' },
  { value: 'KN', label: 'St. Kitts & Nevis', labelRu: 'Сент-Китс и Невис' },
  { value: 'LC', label: 'St. Lucia', labelRu: 'Сент-Люсия' },
  { value: 'MF', label: 'St. Martin', labelRu: 'Сен-Мартен' },
  { value: 'PM', label: 'St. Pierre & Miquelon', labelRu: 'Сен-Пьер и Микелон' },
  { value: 'VC', label: 'St. Vincent & Grenadines', labelRu: 'Сент-Винсент и Гренадины' },
  { value: 'SD', label: 'Sudan', labelRu: 'Судан' },
  { value: 'SR', label: 'Suriname', labelRu: 'Суринам' },
  { value: 'SJ', label: 'Svalbard & Jan Mayen', labelRu: 'Шпицберген и Ян-Майен' },
  { value: 'SE', label: 'Sweden', labelRu: 'Швеция' },
  { value: 'CH', label: 'Switzerland', labelRu: 'Швейцария' },
  { value: 'SY', label: 'Syria', labelRu: 'Сирия' },
  { value: 'TW', label: 'Taiwan', labelRu: 'Тайвань' },
  { value: 'TJ', label: 'Tajikistan', labelRu: 'Таджикистан' },
  { value: 'TZ', label: 'Tanzania', labelRu: 'Танзания' },
  { value: 'TH', label: 'Thailand', labelRu: 'Таиланд' },
  { value: 'TL', label: 'Timor-Leste', labelRu: 'Восточный Тимор' },
  { value: 'TG', label: 'Togo', labelRu: 'Того' },
  { value: 'TK', label: 'Tokelau', labelRu: 'Токелау' },
  { value: 'TO', label: 'Tonga', labelRu: 'Тонга' },
  { value: 'TT', label: 'Trinidad & Tobago', labelRu: 'Тринидад и Тобаго' },
  { value: 'TN', label: 'Tunisia', labelRu: 'Тунис' },
  { value: 'TR', label: 'Türkiye', labelRu: 'Турция' },
  { value: 'TM', label: 'Turkmenistan', labelRu: 'Туркменистан' },
  { value: 'TC', label: 'Turks & Caicos Islands', labelRu: 'Тёркс и Кайкос' },
  { value: 'TV', label: 'Tuvalu', labelRu: 'Тувалу' },
  { value: 'UM', label: 'U.S. Outlying Islands', labelRu: 'Внешние малые острова (США)' },
  { value: 'VI', label: 'U.S. Virgin Islands', labelRu: 'Виргинские острова (США)' },
  { value: 'UG', label: 'Uganda', labelRu: 'Уганда' },
  { value: 'UA', label: 'Ukraine', labelRu: 'Украина' },
  { value: 'AE', label: 'United Arab Emirates', labelRu: 'ОАЭ' },
  { value: 'GB', label: 'United Kingdom', labelRu: 'Великобритания' },
  { value: 'US', label: 'United States', labelRu: 'США' },
  { value: 'UY', label: 'Uruguay', labelRu: 'Уругвай' },
  { value: 'UZ', label: 'Uzbekistan', labelRu: 'Узбекистан' },
  { value: 'VU', label: 'Vanuatu', labelRu: 'Вануату' },
  { value: 'VA', label: 'Vatican City', labelRu: 'Ватикан' },
  { value: 'VE', label: 'Venezuela', labelRu: 'Венесуэла' },
  { value: 'VN', label: 'Vietnam', labelRu: 'Вьетнам' },
  { value: 'WF', label: 'Wallis & Futuna', labelRu: 'Уоллис и Футуна' },
  { value: 'EH', label: 'Western Sahara', labelRu: 'Западная Сахара' },
  { value: 'YE', label: 'Yemen', labelRu: 'Йемен' },
  { value: 'ZM', label: 'Zambia', labelRu: 'Замбия' },
  { value: 'ZW', label: 'Zimbabwe', labelRu: 'Зимбабве' },
];
